import { API_URL, getAuthHeaders } from './config';

const getHeaders = () => {
    return {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
    };
};

export interface Post {
    id: string;
    content?: string;
    image_url?: string;
    author_id: string;
    author_type: 'user' | 'club' | 'circle' | 'shop' | 'federation';
    club_id?: string;
    type: string;
    is_pinned: number;
    created_at: number;
    author_name?: string;
    author_avatar?: string;
    author_currency?: string;
    likes_count: number;
    comments_count: number;
    liked_by_me: boolean;
    user_reaction?: string;
    reactions_summary?: string;
    metadata?: string; // JSON String
    viewer_circle_status?: 'active' | 'pending' | 'applicant' | null;
    circle_applicant_count?: number;
    circle_member_count?: number;
    event_participant_count?: number;
    event_start_date?: number;
    event_max_participants?: number;
    // Flash Sale
    user_claim_code?: string;
    claimed_count?: number;
    author_role?: string;
    // New Fields for Poll/Event
    poll_user_vote?: number;
    poll_vote_stats?: string; // "0:5,1:2"
    event_user_status?: 'interested' | 'going' | null;
    is_edited?: boolean;
    is_deleted?: boolean;
    is_bulk_generated?: boolean;
}

export interface Comment {
    id: string;
    post_id: string;
    user_id: string;
    content: string;
    created_at: number;
    user_name?: string;
    user_avatar?: string;
}

export const feedService = {
    getFeed: async (options: { clubId?: string, federationId?: string, limit?: number, offset?: number, hashtag?: string, searchQuery?: string, userLat?: number, userLon?: number, radius?: number, type?: string } = {}) => {
        const params = new URLSearchParams();
        if (options.clubId) params.append('clubId', options.clubId);
        if (options.federationId) params.append('federationId', options.federationId);
        if (options.limit) params.append('limit', options.limit.toString());
        if (options.offset) params.append('offset', options.offset.toString());
        if (options.hashtag) params.append('hashtag', options.hashtag);
        if (options.searchQuery) params.append('searchQuery', options.searchQuery);
        if (options.userLat) params.append('userLat', options.userLat.toString());
        if (options.userLon) params.append('userLon', options.userLon.toString());
        if (options.radius) params.append('radius', options.radius.toString());
        if (options.type) params.append('type', options.type);

        const res = await fetch(`${API_URL}/feed?${params.toString()}`, {
            headers: getHeaders()
        });
        if (!res.ok) throw new Error('Failed to fetch feed');
        return await res.json() as Post[];
    },

    getTrends: async (limit: number = 5) => {
        const res = await fetch(`${API_URL}/feed/trends?limit=${limit}`, {
            headers: getHeaders()
        });
        if (!res.ok) throw new Error('Failed to fetch trends');
        return await res.json() as { tag: string, count: number }[];
    },

    getPost: async (postId: string) => {
        const res = await fetch(`${API_URL}/feed/${postId}`, {
            headers: getHeaders()
        });
        if (!res.ok) throw new Error('Failed to fetch post');
        return await res.json() as Post;
    },

    createPost: async (data: { content?: string, image_url?: string, club_id?: string, type?: string, is_pinned?: boolean, metadata?: any }, asEntity?: 'club' | 'federation') => {
        let query = '';
        if (asEntity === 'club') query = '?asClub=true';
        if (asEntity === 'federation') query = '?asFederation=true';

        const res = await fetch(`${API_URL}/feed${query}`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.error || 'Failed to create post');
        }
        return await res.json() as Post;
    },

    updatePost: async (postId: string, data: { content?: string, image_url?: string, metadata?: any, is_pinned?: boolean }) => {
        const res = await fetch(`${API_URL}/feed/${postId}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(data)
        });
        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.error || 'Failed to update post');
        }
        return await res.json() as Post;
    },

    toggleLike: async (postId: string, type: string = 'like') => {
        const res = await fetch(`${API_URL}/feed/${postId}/like`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ type })
        });
        if (!res.ok) throw new Error('Failed to toggle like');
        return await res.json() as { liked: boolean, type: string };
    },

    addComment: async (postId: string, content: string, author_name?: string) => {
        const res = await fetch(`${API_URL}/feed/${postId}/comments`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ content, author_name })
        });
        if (!res.ok) throw new Error('Failed to comment');
        return await res.json() as Comment;
    },

    getComments: async (postId: string) => {
        const res = await fetch(`${API_URL}/feed/${postId}/comments`, {
            headers: getHeaders()
        });
        if (!res.ok) throw new Error('Failed to fetch comments');
        return await res.json() as Comment[];
    },

    deletePost: async (postId: string) => {
        const res = await fetch(`${API_URL}/feed/${postId}`, {
            method: 'DELETE',
            headers: getHeaders()
        });
        if (!res.ok) throw new Error('Failed to delete post');
        return await res.json();
    },

    reportPost: async (postId: string, reason: string) => {
        const res = await fetch(`${API_URL}/feed/${postId}/report`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ reason })
        });
        if (!res.ok) throw new Error('Failed to report post');
        return await res.json();
    },
    deleteComment: async (commentId: string) => {
        const response = await fetch(`${API_URL}/feed/comments/${commentId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error('Failed to delete comment');
    },
    reportComment: async (commentId: string, reason: string) => {
        const response = await fetch(`${API_URL}/feed/comments/${commentId}/report`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ reason }),
        });
        if (!response.ok) throw new Error('Failed to report comment');
    },

    votePoll: async (postId: string, optionIndex: number) => {
        const res = await fetch(`${API_URL}/feed/${postId}/vote`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ optionIndex })
        });
        if (!res.ok) throw new Error('Failed to vote');
        return await res.json();
    },

    registerEvent: async (postId: string, status: 'interested' | 'going') => {
        const res = await fetch(`${API_URL}/feed/${postId}/event/register`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ status })
        });
        if (!res.ok) throw new Error('Failed to register for event');
        return await res.json();
    },

    followHashtag: async (tag: string) => {
        const res = await fetch(`${API_URL}/feed/hashtags/follow`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ tag })
        });
        if (!res.ok) throw new Error('Failed to follow hashtag');
        return await res.json();
    },

    unfollowHashtag: async (tag: string) => {
        const res = await fetch(`${API_URL}/feed/hashtags/unfollow`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ tag })
        });
        if (!res.ok) throw new Error('Failed to unfollow hashtag');
        return await res.json();
    },

    claimFlashSale: async (postId: string) => {
        const res = await fetch(`${API_URL}/feed/${postId}/claim-flash-sale`, {
            method: 'POST',
            headers: getHeaders()
        });
        if (!res.ok) {
            const err = await res.json() as any;
            throw new Error(err.error || "Failed to claim");
        }
        return await res.json() as { success: boolean, code?: string, error?: string };
    },

    getFollowedHashtags: async () => {
        const res = await fetch(`${API_URL}/feed/hashtags/followed`, {
            headers: getHeaders()
        });
        if (!res.ok) throw new Error('Failed to fetch followed hashtags');
        return await res.json() as string[];
    }
};
